import { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { Sheet, PrimaryBtn, GhostBtn } from './UI';
import { fmtDate } from '../utils/constants';

// Get most recent Saturday on or before a given date
function lastSaturday(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  const day = d.getDay(); // 0=Sun, 6=Sat
  const diff = day === 6 ? 0 : day + 1; // days back to Saturday
  d.setDate(d.getDate() - diff);
  return d.toISOString().split('T')[0];
}

function addDays(dateStr, n) {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
}

function today() {
  return new Date().toISOString().split('T')[0];
}

function inRange(date, start, end) {
  return date >= start && date <= end;
}

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h);
  return `${hour % 12 || 12}:${m} ${hour < 12 ? 'AM' : 'PM'}`;
}

export function ReportModal({ store, onClose }) {
  const defaultStart = lastSaturday(today());
  const [startDate, setStartDate] = useState(defaultStart);
  const endDate = addDays(startDate, 6); // always Sat–Fri (6 days later)

  const [calEvents, setCalEvents] = useState([]);
  useEffect(() => {
    api.getCalendar(startDate, endDate).then(data => setCalEvents(data?.events || []));
  }, [startDate]);

  const tasks = store.tasks || [];
  const logEntries = store.logEntries || [];
  const todayItems = store.todayItems || [];

  // ── Section 0: Created or Progressed This Week ───────────────────────────
  // Active tasks/trackers created this week
  const createdThisWeek = tasks.filter(t =>
    t.status !== 'Complete' &&
    t.dateCreated &&
    inRange(t.dateCreated.slice(0, 10), startDate, endDate)
  );
  // Active tasks/trackers whose dateUpdated falls in range but were NOT created this week
  const progressedThisWeek = tasks.filter(t =>
    t.status !== 'Complete' &&
    t.dateUpdated &&
    inRange(t.dateUpdated.slice(0, 10), startDate, endDate) &&
    !(t.dateCreated && inRange(t.dateCreated.slice(0, 10), startDate, endDate))
  );

  // ── Section 1: Completed Tasks ────────────────────────────────────────────
  const completedTasks = tasks.filter(t =>
    t.type !== 'tracker' &&
    t.status === 'Complete' &&
    t.dateResolved &&
    inRange(t.dateResolved.slice(0, 10), startDate, endDate)
  );

  // ── Section 2: Resolved Trackers ─────────────────────────────────────────
  const resolvedTrackers = tasks.filter(t =>
    t.type === 'tracker' &&
    t.status === 'Complete' &&
    t.dateResolved &&
    inRange(t.dateResolved.slice(0, 10), startDate, endDate)
  );
  const resolvedIncidentTrackers = resolvedTrackers.filter(t => t.subtype !== 'tracker-task');
  const resolvedTaskTrackers = resolvedTrackers.filter(t => t.subtype === 'tracker-task');

  // ── Section 3: Today items worked on ─────────────────────────────────────
  const workedOnItems = todayItems.filter(i =>
    i.checked && i.date && inRange(i.date, startDate, endDate)
  ).sort((a, b) => a.person.localeCompare(b.person) || a.date.localeCompare(b.date));

  // Group by person
  const workedByPerson = {};
  workedOnItems.forEach(i => {
    if (!workedByPerson[i.person]) workedByPerson[i.person] = [];
    workedByPerson[i.person].push(i);
  });

  // ── Section 4: Hours Log ─────────────────────────────────────────────────
  const weekLog = logEntries.filter(e =>
    e.date && inRange(e.date, startDate, endDate)
  ).sort((a, b) => a.person.localeCompare(b.person) || a.date.localeCompare(b.date) || a.startTime.localeCompare(b.startTime));

  const logByPerson = {};
  weekLog.forEach(e => {
    if (!logByPerson[e.person]) logByPerson[e.person] = [];
    logByPerson[e.person].push(e);
  });

  // ── Build report text ─────────────────────────────────────────────────────
  const lines = [];
  lines.push(`SNP ENGINEERING — WEEKLY SUMMARY`);
  lines.push(`Week of ${fmtDate(startDate)} – ${fmtDate(endDate)}`);
  lines.push(`Generated: ${new Date().toLocaleDateString()}`);
  lines.push('');
  lines.push('═'.repeat(50));

  // Section 0 - Created or Progressed
  lines.push('');
  lines.push('CREATED OR PROGRESSED THIS WEEK');
  lines.push('─'.repeat(30));
  if (createdThisWeek.length === 0 && progressedThisWeek.length === 0) {
    lines.push('  None this week.');
  } else {
    if (createdThisWeek.length > 0) {
      lines.push('  New this week:');
      createdThisWeek.forEach((t, i) => {
        const badge = t.type === 'tracker' ? '[Tracker]' : '[Task]';
        lines.push(`    ${i + 1}. ${badge} ${t.title}`);
        if (t.assignees?.length) lines.push(`       Assigned: ${t.assignees.join(', ')}`);
        if (t.status) lines.push(`       Status: ${t.status}`);
      });
    }
    if (progressedThisWeek.length > 0) {
      if (createdThisWeek.length > 0) lines.push('');
      lines.push('  Updated/progressed:');
      progressedThisWeek.forEach((t, i) => {
        const badge = t.type === 'tracker' ? '[Tracker]' : '[Task]';
        lines.push(`    ${i + 1}. ${badge} ${t.title}`);
        if (t.assignees?.length) lines.push(`       Assigned: ${t.assignees.join(', ')}`);
        if (t.status) lines.push(`       Status: ${t.status}`);
      });
    }
  }

  // Section 1
  lines.push('');
  lines.push('COMPLETED TASKS');
  lines.push('─'.repeat(30));
  if (completedTasks.length === 0) {
    lines.push('  None this week.');
  } else {
    completedTasks.forEach((t, i) => {
      lines.push(`  ${i + 1}. ${t.title}`);
      if (t.assignees?.length) lines.push(`     Assigned: ${t.assignees.join(', ')}`);
      if (t.completedBy) lines.push(`     Closed by: ${t.completedBy}`);
      lines.push(`     Resolved: ${fmtDate(t.dateResolved)}`);
      if (t.completionNote) lines.push(`     Completion Note: ${t.completionNote}`);
    });
  }

  // Section 2a - Incident Trackers
  lines.push('');
  lines.push('RESOLVED INCIDENT TRACKERS');
  lines.push('─'.repeat(30));
  if (resolvedIncidentTrackers.length === 0) {
    lines.push('  None this week.');
  } else {
    resolvedIncidentTrackers.forEach((t, i) => {
      lines.push(`  ${i + 1}. ${t.title}`);
      if (t.assignees?.length) lines.push(`     Assigned: ${t.assignees.join(', ')}`);
      lines.push(`     Resolved: ${fmtDate(t.dateResolved)}`);
      if (t.notes) lines.push(`     Description: ${t.notes}`);
    });
  }

  // Section 2b - Task Trackers
  lines.push('');
  lines.push('RESOLVED TASK TRACKERS');
  lines.push('─'.repeat(30));
  if (resolvedTaskTrackers.length === 0) {
    lines.push('  None this week.');
  } else {
    resolvedTaskTrackers.forEach((t, i) => {
      lines.push(`  ${i + 1}. ${t.title}`);
      if (t.assignees?.length) lines.push(`     Assigned: ${t.assignees.join(', ')}`);
      lines.push(`     Resolved: ${fmtDate(t.dateResolved)}`);
      if (t.notes) lines.push(`     Description: ${t.notes}`);
    });
  }

  // Section 3
  lines.push('');
  lines.push('DAILY FOCUS — WORKED ON THIS WEEK');
  lines.push('─'.repeat(30));
  const personNames = Object.keys(workedByPerson).sort();
  if (personNames.length === 0) {
    lines.push('  No focus items marked as worked on this week.');
  } else {
    personNames.forEach(person => {
      lines.push(`  ${person}:`);
      workedByPerson[person].forEach(i => {
        const taskRef = i.taskId ? tasks.find(t => t.id === i.taskId) : null;
        const label = taskRef ? taskRef.title : i.customText;
        lines.push(`    • [${fmtDate(i.date)}] ${label}`);
        if (i.note) lines.push(`      Note: ${i.note}`);
      });
    });
  }

  // Section 4
  lines.push('');
  lines.push('HOURS LOG');
  lines.push('─'.repeat(30));
  const logPersons = Object.keys(logByPerson).sort();
  if (logPersons.length === 0) {
    lines.push('  No hours logged this week.');
  } else {
    logPersons.forEach(person => {
      const entries = logByPerson[person];
      const totalMins = entries.reduce((sum, e) => {
        const [sh, sm] = e.startTime.split(':');
        const [eh, em] = e.endTime.split(':');
        const mins = (parseInt(eh) * 60 + parseInt(em)) - (parseInt(sh) * 60 + parseInt(sm));
        return sum + (mins > 0 ? mins : 0);
      }, 0);
      const hrs = Math.floor(totalMins / 60);
      const mins = totalMins % 60;
      const totalStr = `${hrs}${mins > 0 ? `:${String(mins).padStart(2, '0')}` : ''}h`;
      lines.push(`  ${person} (${totalStr} total):`);
      entries.forEach(e => {
        lines.push(`    • [${fmtDate(e.date)} ${formatTime(e.startTime)}–${formatTime(e.endTime)}] ${e.note}`);
      });
    });
  }

  // ── Section 5: Schedule ─────────────────────────────────────────────────
  const PEOPLE = ['Ray', 'Adam', 'Diana'];
  const weekDates = Array.from({ length: 7 }, (_, i) => addDays(startDate, i));
  const DOW_SHORT = ['Sat','Sun','Mon','Tue','Wed','Thu','Fri'];

  const getDayLabel = (ev, person) => {
    if (ev.day_type === 'event') return ev.game1_opponent || 'Event';
    const shifts = (ev.shifts || []).filter(s => s.person === person);
    if (shifts.length === 0) return null;
    const hasWork = shifts.some(s => s.shift && s.shift !== 'OFF');
    if (!hasWork) return 'Off Day';
    const isBoth = ev.team === 'both';
    if (isBoth) {
      const parts = [];
      if (ev.game1_opponent) parts.push(`Pens ${ev.game1_location || ''}`.trim());
      if (ev.game2_opponent) parts.push(`Bucs ${ev.game2_location || ''}`.trim());
      return parts.join(' / ') || 'Game Day';
    }
    const team = ev.team === 'pens' ? 'Pens' : 'Bucs';
    return `${team} ${ev.game1_location || ''}`.trim() || 'Game Day';
  };

  lines.push('');
  lines.push('SCHEDULE');
  lines.push('─'.repeat(30));
  PEOPLE.forEach(person => {
    const days = [];
    weekDates.forEach((date, i) => {
      const ev = calEvents.find(e => e.date === date);
      if (!ev) return;
      const label = getDayLabel(ev, person);
      if (label) days.push(`${DOW_SHORT[i]} (${label})`);
    });
    lines.push(`  ${person}: ${days.length > 0 ? days.join(', ') : 'No days entered'}`);
  });

  lines.push('');
  lines.push('═'.repeat(50));

  const reportText = lines.join('\n');

  const copy = () => navigator.clipboard?.writeText(reportText).then(() => alert('Copied!'));

  const totalItems = createdThisWeek.length + progressedThisWeek.length + completedTasks.length + resolvedTrackers.length + workedOnItems.length + weekLog.length;

  return (
    <Sheet
      title="Weekly Report"
      onClose={onClose}
      footer={<><GhostBtn onClick={onClose}>Close</GhostBtn><PrimaryBtn onClick={copy}>Copy to Clipboard</PrimaryBtn></>}
    >
      {/* Date range picker */}
      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <div style={label}>Start Date (Saturday)</div>
          <input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            style={input}
          />
        </div>
        <div style={{ flex: 1 }}>
          <div style={label}>End Date</div>
          <input
            type="date"
            value={endDate}
            readOnly
            style={{ ...input, color: '#94a3b8' }}
          />
        </div>
      </div>

      <div style={{ fontSize: 13, color: '#64748b' }}>
        {totalItems} item{totalItems !== 1 ? 's' : ''} across {fmtDate(startDate)} – {fmtDate(endDate)}
        {' · '}{createdThisWeek.length + progressedThisWeek.length} active · {completedTasks.length} completed · {resolvedTrackers.length} trackers resolved · {workedOnItems.length} focus · {weekLog.length} log
      </div>

      <pre style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, fontSize: 12, whiteSpace: 'pre-wrap', lineHeight: 1.6, color: '#334155', maxHeight: 400, overflowY: 'auto', fontFamily: 'monospace' }}>
        {reportText}
      </pre>
    </Sheet>
  );
}

const label = { fontSize: 12, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 };
const input = { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', fontSize: 14, color: '#0f172a', outline: 'none', width: '100%', boxSizing: 'border-box' };
